// frontend/src/pages/commissions/CommissionDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Badge, Dropdown, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHeader from '../../components/common/PageHeader';
import { Line, Doughnut } from 'react-chartjs-2';
import { saleCommissionsV2API } from '../../services/api/v2/saleCommissions';
import ProductsServices from '../../services/api/products-stable';
import './CommissionDashboard.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const CommissionDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30'); // dias
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [products, setProducts] = useState([]);

  // Carregar dados do dashboard (V2: /api/v2/sale-commissions/dashboard)
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        const response = await saleCommissionsV2API.dashboard({
          period,
          product: selectedProduct,
        });
        if (response.data?.success) {
          setDashboardData(response.data.data);
        }
      } catch (error) {
        console.error('Erro ao carregar dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [period, selectedProduct]);

  // Carregar produtos do usuário (V2: productsV2API.listMine via ProductsServices)
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const list = await ProductsServices.getProducts();
        setProducts(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
      }
    };

    loadProducts();
  }, []);

  // Formatação de moeda
  const formatCurrency = (amountInCents) => {
    return (amountInCents / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  // Configurações do gráfico de evolução
  const evolutionChartData = {
    labels: dashboardData?.evolution?.labels || [],
    datasets: [
      {
        label: 'Comissões Recebidas',
        data: dashboardData?.evolution?.commissions || [],
        borderColor: '#ff6b35', // Laranja mais vivo
        backgroundColor: 'rgba(255, 107, 53, 0.15)', // Laranja mais vivo com transparência
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Vendas Geradas',
        data: dashboardData?.evolution?.sales || [],
        borderColor: '#3b82f6', // Azul mais vibrante
        backgroundColor: 'rgba(59, 130, 246, 0.15)', // Azul com mais transparência
        tension: 0.4,
        fill: true,
      }
    ],
  };

  const evolutionChartOptions = {
    // responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Evolução de Comissões e Vendas',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    }
  };

  // Configurações do gráfico de distribuição por tipo
  const distributionChartData = {
    labels: ['Produtor', 'Afiliado', 'Coprodutor', 'Gerente'],
    datasets: [
      {
        label: 'Comissões por Tipo',
        data: [
          dashboardData?.distribution?.producer || 0,
          dashboardData?.distribution?.affiliate || 0,
          dashboardData?.distribution?.coproducer || 0,
          dashboardData?.distribution?.manager || 0,
        ],
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 205, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 205, 86, 1)',
          'rgba(75, 192, 192, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const distributionChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
      },
      title: {
        display: true,
        text: 'Distribuição de Comissões por Tipo',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${formatCurrency(context.parsed)}`;
          }
        }
      }
    },
  };

  if (loading) {
    return (
      <MasterLayout>
        <div className="dashboard-main-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
            <h6 className="fw-semibold mb-0">Dashboard de Comissões</h6>
            <Breadcrumb
              items={[
                { label: 'Home', link: '/Dashboard' },
                { label: 'Afiliados', link: '/my-affiliates' },
                { label: 'Dashboard Comissões', link: '/commissions/dashboard' },
              ]}
            />
          </div>
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="text-muted">Carregando dashboard de comissões...</p>
          </div>
        </div>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb 
        title="Dashboard de Comissões" 
        items={[
          { label: 'Início', path: '/Dashboard' },
          { label: 'Comissões', path: '/commissions/dashboard' }
        ]}
      />

      {/* Header Section */}
      <PageHeader
        icon="mdi:chart-line"
        title="Dashboard de Comissões"
        subtitle="Acompanhe suas comissões, vendas e performance de afiliados em tempo real"
      />

      {/* Filtros */}
      <div className="d-flex gap-2 mb-24">
        <Dropdown>
          <Dropdown.Toggle variant="outline-primary" size="sm" className="icon-card">
            <Icon icon="mdi:calendar" className="me-1" />
            {period === '7' ? '7 dias' : period === '30' ? '30 dias' : '90 dias'}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => setPeriod('7')}>Últimos 7 dias</Dropdown.Item>
            <Dropdown.Item onClick={() => setPeriod('30')}>Últimos 30 dias</Dropdown.Item>
            <Dropdown.Item onClick={() => setPeriod('90')}>Últimos 90 dias</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
        
        <Dropdown>
          <Dropdown.Toggle variant="outline-secondary" size="sm" className="icon-card">
            <Icon icon="mdi:package" className="me-1" />
            {selectedProduct === 'all' ? 'Todos os produtos' : products.find(p => p.id == selectedProduct)?.name}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => setSelectedProduct('all')}>Todos os produtos</Dropdown.Item>
            {products.map(product => (
              <Dropdown.Item key={product.id} onClick={() => setSelectedProduct(product.id)}>
                {product.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Cards de Resumo - Padrão Dashboard */}
      <Row className="g-3" style={{ marginBottom: '24px' }}>
        <Col md={3}>
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="flex-grow-1">
                  <h6 className="card-title text-muted mb-2" style={{ fontSize: '12px', fontWeight: '500' }}>Total Comissões</h6>
                  <span className="mb-1 d-block" style={{ fontSize: '20px', fontWeight: 'bold',  }}>
                    {formatCurrency(dashboardData?.summary?.total_commissions || 0)}
                  </span>
                  <small className="text-primary" style={{ fontSize: '11px', fontWeight: '500' }}>
                    Comissões recebidas
                  </small>
                </div>
                <div className="bg-primary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                  <Icon icon="mdi:currency-brl" className="text-primary" style={{ fontSize: '20px' }} />
                </div>
              </div>
            </div>
          </div>
        </Col>
        
        <Col md={3}>
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="flex-grow-1">
                  <h6 className="card-title text-muted mb-2" style={{ fontSize: '12px', fontWeight: '500' }}>Vendas Geradas</h6>
                  <span className="mb-1 d-block" style={{ fontSize: '20px', fontWeight: 'bold',  }}>
                    {dashboardData?.summary?.total_sales || 0}
                  </span>
                  <small className="text-success" style={{ fontSize: '11px', fontWeight: '500' }}>
                    Vendas realizadas
                  </small>
                </div>
                <div className="bg-success bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                  <Icon icon="mdi:chart-line-variant" className="text-success" style={{ fontSize: '20px' }} />
                </div>
              </div>
            </div>
          </div>
        </Col>
        
        <Col md={3}>
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="flex-grow-1">
                  <h6 className="card-title text-muted mb-2" style={{ fontSize: '12px', fontWeight: '500' }}>Saldo Pendente</h6>
                  <span className="mb-1 d-block" style={{ fontSize: '20px', fontWeight: 'bold',  }}>
                    {formatCurrency(dashboardData?.summary?.pending_balance || 0)}
                  </span>
                  <small className="text-warning" style={{ fontSize: '11px', fontWeight: '500' }}>
                    Em análise
                  </small>
                </div>
                <div className="bg-warning bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                  <Icon icon="mdi:clock-outline" className="text-warning" style={{ fontSize: '20px' }} />
                </div>
              </div>
            </div>
          </div>
        </Col>
        
        <Col md={3}>
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="flex-grow-1">
                  <h6 className="card-title text-muted mb-2 card-title-truncate" style={{ fontSize: '12px', fontWeight: '500' }}>Disponível para Saque</h6>
                  <span className="mb-1 d-block" style={{ fontSize: '20px', fontWeight: 'bold',  }}>
                    {formatCurrency(dashboardData?.summary?.available_balance || 0)}
                  </span>
                  <small className="text-info" style={{ fontSize: '11px', fontWeight: '500' }}>
                    Disponível para saque
                  </small>
                </div>
                <div className="bg-info bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                  <Icon icon="mdi:wallet" className="text-info" style={{ fontSize: '20px' }} />
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Gráficos */}
      <Row className="g-3" style={{ marginBottom: '24px' }}>
        <Col lg={8}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom">
              <Card.Title className="mb-0">
                <Icon icon="mdi:chart-line" className="me-2" />
                Evolução Temporal
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Line data={evolutionChartData} options={evolutionChartOptions} height={100} />
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom">
              <Card.Title className="mb-0">
                <Icon icon="mdi:chart-donut" className="me-2" />
                Distribuição por Tipo
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Doughnut data={distributionChartData} options={distributionChartOptions} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabela de Comissões Recentes */}
      <Row>
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
              <Card.Title className="mb-0">
                <Icon icon="mdi:history" className="me-2" />
                Comissões Recentes
              </Card.Title>
              <Button variant="outline-primary" size="sm">
                <Icon icon="mdi:download" className="me-1" />
                Exportar
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="align-middle commissions-table">
                <thead className="table-light">
                  <tr>
                    <th>Data</th>
                    <th>Produto</th>
                    <th>Tipo</th>
                    <th>Valor</th>
                    <th>Status</th>
                    <th>Disponível em</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.recent_commissions?.map((commission, index) => (
                    <tr key={index}>
                      <td>
                        <small className="text-muted">
                          {new Date(commission.created_at).toLocaleDateString('pt-BR')}
                        </small>
                      </td>
                      <td>
                        <div>
                          <strong>{commission.product_name}</strong>
                          <br />
                          <small className="text-muted">Venda #{commission.sale_id}</small>
                        </div>
                      </td>
                      <td>
                        <Badge bg={
                          commission.role_type === 'producer' ? 'primary' :
                          commission.role_type === 'affiliate' ? 'info' :
                          commission.role_type === 'coproducer' ? 'success' :
                          'warning'
                        }>
                          {
                            commission.role_type === 'producer' ? 'Produtor' :
                            commission.role_type === 'affiliate' ? 'Afiliado' :
                            commission.role_type === 'coproducer' ? 'Coprodutor' :
                            'Gerente'
                          }
                        </Badge>
                      </td>
                      <td>
                        <strong className="text-success">
                          {formatCurrency(commission.commission_amount)}
                        </strong>
                      </td>
                      <td>
                        <Badge bg={commission.is_paid ? 'success' : 'warning'}>
                          {commission.is_paid ? 'Pago' : 'Pendente'}
                        </Badge>
                      </td>
                      <td>
                        <small className="text-muted">
                          {commission.available_at 
                            ? new Date(commission.available_at).toLocaleDateString('pt-BR')
                            : 'Imediato'
                          }
                        </small>
                      </td>
                    </tr>
                  )) || []}
                </tbody>
              </Table>
              
              {(!dashboardData?.recent_commissions || dashboardData.recent_commissions.length === 0) && (
                <div className="text-center py-5">
                  <Icon icon="mdi:chart-line-variant" className="fs-1 text-muted mb-3" />
                  <h6 className="text-muted">Nenhuma comissão encontrada</h6>
                  <p className="text-muted">
                    As comissões aparecerão aqui conforme as vendas forem sendo processadas
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </MasterLayout>
  );
};

export default CommissionDashboard;
